import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listStoneRequirements, listStoneQuotes, listStoneSuppliers,
  acceptStoneQuote, rejectStoneQuote, requestStoneQuoteRevision, getImageUrl,
  formatApiError,
} from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import StonesHeader from '@/components/stones/StonesHeader';
import HeroThumb from '@/components/stones/HeroThumb';
import StoneQuoteChat from '@/components/stones/StoneQuoteChat';
import ReviseQuoteDialog from '@/components/stones/ReviseQuoteDialog';
import {
  STONE_REQUIREMENT_STATUSES, STONE_QUOTE_STATUSES, STONE_KINDS, CERTIFICATE_TYPES,
} from '@/utils/constants';
import {
  Gem, Plus, Check, X, Send, MessageCircle, Phone, Search,
  ArrowLeft, MessageSquare, Loader2,
} from 'lucide-react';

const REQ_STATUS = Object.fromEntries(STONE_REQUIREMENT_STATUSES.map(s => [s.value, s]));
const KIND_LABEL = Object.fromEntries(STONE_KINDS.map(k => [k.value, k.label]));
const CERT_LABEL = Object.fromEntries(CERTIFICATE_TYPES.map(c => [c.value, c.label]));

function buildWaLink(phone, message) {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10) digits = '91' + digits;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

// Three-pane workspace: requirements (left) → quotes (middle) → chat (right).
// URL state: ?req=<id>&quote=<id> so admins can deep-link and bookmark.
// On screens narrower than lg we collapse to a single pane with mobileView tabs.
export default function StoneWorkspacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSupplier = user?.role === 'stone_supplier';

  const [requirements, setRequirements] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [reviseQuote, setReviseQuote] = useState(null);
  const [acting, setActing] = useState(null);
  const [mobileView, setMobileView] = useState('left'); // left | middle | right

  const selectedReqId = searchParams.get('req');
  const selectedQuoteId = searchParams.get('quote');
  const selectedReq = requirements.find(r => r.id === selectedReqId) || null;

  // Quotes belonging to the selected requirement.
  const reqQuotes = useMemo(
    () => quotes.filter(q => q.requirement_id === selectedReqId),
    [quotes, selectedReqId]
  );
  const selectedQuote = reqQuotes.find(q => q.id === selectedQuoteId) || null;

  const setReq = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('req', id); else next.delete('req');
    next.delete('quote'); // clear quote selection when switching requirement
    setSearchParams(next, { replace: true });
    setMobileView('middle');
  }, [searchParams, setSearchParams]);

  const setQuote = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('quote', id); else next.delete('quote');
    setSearchParams(next, { replace: true });
    if (id) setMobileView('right');
  }, [searchParams, setSearchParams]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, q] = await Promise.all([
        listStoneRequirements(),
        listStoneQuotes(),
      ]);
      setRequirements(r.data.data || []);
      setQuotes(q.data.data || []);
      if (isAdmin) {
        const s = await listStoneSuppliers();
        setSuppliers(s.data.data || []);
      }
    } catch (e) {
      toast.error('Failed to load workspace');
    } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filtered + searched requirements for the left pane
  const filteredReqs = useMemo(() => {
    let list = requirements;
    if (filter) list = list.filter(r => r.status === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(r =>
        r.requirement_no?.toLowerCase().includes(s) ||
        r.gemstone_type?.toLowerCase().includes(s) ||
        r.notes?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [requirements, filter, search]);

  const handleAccept = async (q) => {
    if (!window.confirm(`Accept ${q.supplier_name}'s quote? The requirement will be closed.`)) return;
    setActing(q.id);
    try { await acceptStoneQuote(q.id); toast.success('Quote accepted'); fetchAll(); }
    catch (e) { toast.error(formatApiError(e, 'Failed')); }
    finally { setActing(null); }
  };
  const handleReject = async (q) => {
    setActing(q.id);
    try { await rejectStoneQuote(q.id); toast.success('Quote rejected'); fetchAll(); }
    catch (e) { toast.error(formatApiError(e, 'Failed')); }
    finally { setActing(null); }
  };
  const handleRevise = async (payload) => {
    if (!reviseQuote) return;
    try {
      await requestStoneQuoteRevision(reviseQuote.id, payload);
      toast.success(`Revision requested from ${reviseQuote.supplier_name}`);
      setReviseQuote(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e, 'Failed')); }
  };

  const summary = requirements.length === 0
    ? 'Stone procurement workspace.'
    : `${requirements.filter(r => r.status === 'open').length} open · ${quotes.filter(q => q.status === 'submitted').length} pending quotes · ${requirements.length} total`;

  return (
    <CollapsibleLayout>
      <StoneTabs active="requirements" />
      <StonesHeader icon={Gem} title="Stone Requirements" subtitle={summary}>
        {isAdmin && (
          <Button size="sm" onClick={() => navigate('/stones/new')} className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="header-new-req">
            <Plus size={13} className="mr-1" /> New
          </Button>
        )}
      </StonesHeader>

      {/* Mobile pane switcher */}
      <div className="lg:hidden flex border-b bg-white sticky top-[88px] z-20" data-testid="mobile-pane-switcher">
        {[
          { key: 'left', label: 'Requirements' },
          { key: 'middle', label: selectedReq ? `Quotes (${reqQuotes.length})` : 'Quotes' },
          { key: 'right', label: 'Chat' },
        ].map(p => (
          <button key={p.key} onClick={() => setMobileView(p.key)}
            disabled={(p.key === 'middle' && !selectedReq) || (p.key === 'right' && !selectedQuote)}
            className={`flex-1 px-2 py-2 text-xs font-medium border-b-2 transition-colors disabled:opacity-40 ${
              mobileView === p.key ? 'text-teal-700 border-teal-600' : 'text-slate-500 border-transparent'
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-0 lg:gap-3 lg:p-3 min-h-[calc(100vh-160px)]">

        {/* === LEFT PANE: requirements list === */}
        <aside className={`${mobileView === 'left' ? 'block' : 'hidden'} lg:block lg:col-span-4 xl:col-span-3 bg-white lg:rounded-lg lg:border flex flex-col`} data-testid="left-pane">
          <div className="px-3 py-2 border-b flex items-center gap-2">
            <div className="flex-1 relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search requirement, stone…"
                className="h-8 pl-7 text-xs" data-testid="workspace-search" />
            </div>
            {isAdmin && (
              <Button size="sm" onClick={() => navigate('/stones/new')} className="bg-teal-600 hover:bg-teal-700 h-8 text-xs px-2.5" data-testid="workspace-new-req">
                <Plus size={13} className="mr-1" /> New
              </Button>
            )}
          </div>
          <div className="px-3 py-1.5 border-b flex gap-1 overflow-x-auto">
            {[{ value: '', label: 'All' }, ...STONE_REQUIREMENT_STATUSES].map(s => (
              <button key={s.value} onClick={() => setFilter(s.value)}
                className={`text-[10px] px-2 py-0.5 rounded whitespace-nowrap ${filter === s.value ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                data-testid={`workspace-filter-${s.value || 'all'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-xs text-muted-foreground text-center py-8">Loading…</p>
            ) : filteredReqs.length === 0 ? (
              <div className="text-center py-10 px-4">
                <Gem size={36} className="mx-auto text-slate-300 mb-2" />
                <p className="text-xs text-muted-foreground">{requirements.length === 0 ? 'No requirements yet.' : 'No matches for current filter.'}</p>
              </div>
            ) : (
              <ul>
                {filteredReqs.map(r => {
                  const status = REQ_STATUS[r.status] || { label: r.status, color: '' };
                  const qCount = quotes.filter(q => q.requirement_id === r.id).length;
                  const submittedCount = quotes.filter(q => q.requirement_id === r.id && q.status === 'submitted').length;
                  const isActive = r.id === selectedReqId;
                  return (
                    <li key={r.id}>
                      <button onClick={() => setReq(r.id)}
                        className={`relative w-full text-left px-3 py-2.5 pl-4 border-b border-slate-100 transition-colors flex gap-2.5 hover:bg-slate-50 ${
                          isActive ? 'bg-teal-50' :
                          r.status === 'cancelled' ? 'opacity-60' : ''
                        }`}
                        data-testid={`workspace-req-${r.id}`}>
                        {/* Status side-band — same pattern as quote cards for visual consistency */}
                        <span className={`absolute left-0 top-0 bottom-0 w-1 ${
                          isActive ? 'bg-teal-600' :
                          submittedCount > 0 ? 'bg-amber-400' :
                          r.status === 'closed' ? 'bg-emerald-500' :
                          r.status === 'cancelled' ? 'bg-slate-300' :
                          'bg-transparent'
                        }`} aria-hidden />
                        <HeroThumb image={r.sample_images?.[0]} size={48} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono font-bold text-xs">{r.requirement_no}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded border ${status.color}`}>{status.label}</span>
                          </div>
                          <div className="text-sm font-medium mt-0.5 truncate">{r.gemstone_type}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {KIND_LABEL[r.stone_kind] || r.stone_kind}
                            {r.size_mm ? ` · ${r.size_mm}` : ''}
                            {r.qty ? ` · Qty ${r.qty}` : ''}
                          </div>
                          {qCount > 0 && (
                            <div className="text-[10px] mt-1 text-slate-600">
                              {qCount} quote{qCount === 1 ? '' : 's'}
                              {submittedCount > 0 && <span className="ml-1 text-amber-700 font-medium">· {submittedCount} pending</span>}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* === MIDDLE PANE: quotes for selected requirement === */}
        <section className={`${mobileView === 'middle' ? 'block' : 'hidden'} lg:block lg:col-span-5 xl:col-span-6 bg-white lg:rounded-lg lg:border flex flex-col`} data-testid="middle-pane">
          {!selectedReq ? (
            <div className="flex-1 flex items-center justify-center p-10 text-center text-sm text-muted-foreground">
              Pick a requirement on the left to view quotes.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b">
                <div className="flex items-start gap-3">
                  {/* Hero strip — show the client-supplied sample images so admin can
                      compare every supplier's quoted stone against the original request
                      side-by-side without leaving the workspace. */}
                  {selectedReq.sample_images?.length > 0 ? (
                    <div className="flex gap-1.5 shrink-0">
                      {selectedReq.sample_images.slice(0, 3).map((p, i) => (
                        <a key={i} href={getImageUrl(p)} target="_blank" rel="noreferrer"
                          className="block w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all"
                          title={`Sample ${i + 1}`}>
                          <img src={getImageUrl(p)} alt="" className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        </a>
                      ))}
                      {selectedReq.sample_images.length > 3 && (
                        <span className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-[11px] font-medium text-slate-500">
                          +{selectedReq.sample_images.length - 3}
                        </span>
                      )}
                    </div>
                  ) : (
                    <HeroThumb image={null} size={64} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-sm">{selectedReq.requirement_no}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${(REQ_STATUS[selectedReq.status] || {}).color || ''}`}>{(REQ_STATUS[selectedReq.status] || {}).label || selectedReq.status || '—'}</span>
                    </div>
                    <h2 className="text-base font-semibold mt-1">{selectedReq.gemstone_type || 'Untitled'}</h2>
                    <div className="text-xs text-muted-foreground">
                      {KIND_LABEL[selectedReq.stone_kind] || selectedReq.stone_kind || '—'}
                      {selectedReq.size_mm ? ` · ${selectedReq.size_mm}` : ''}
                      {selectedReq.qty ? ` · Qty ${selectedReq.qty}` : ''}
                      {selectedReq.total_ctw ? ` · ${selectedReq.total_ctw} ct` : ''}
                      {selectedReq.certified && selectedReq.certificate_type && selectedReq.certificate_type !== 'none' ? ` · Cert: ${CERT_LABEL[selectedReq.certificate_type] || selectedReq.certificate_type}` : ''}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/stones/${selectedReq.id}`)} className="text-xs h-7 shrink-0" data-testid="view-detail">Detail</Button>
                </div>
                {selectedReq.notes && (
                  <p className="mt-2 text-[11px] text-slate-600 italic line-clamp-2">{selectedReq.notes}</p>
                )}

                {isAdmin && selectedReq.assigned_supplier_ids?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium flex items-center gap-1 mb-1.5">
                      <MessageCircle size={11} /> Notify on WhatsApp
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(selectedReq.assigned_supplier_ids || []).map(sid => {
                        const supp = suppliers.find(s => s.id === sid);
                        if (!supp) return null;
                        const reqUrl = `${window.location.origin}/stones/${selectedReq.id}`;
                        const waMsg = `Hi ${supp.name}, new stone requirement: ${selectedReq.requirement_no} — ${selectedReq.gemstone_type}` +
                                      `${selectedReq.size_mm ? ` ${selectedReq.size_mm}` : ''}${selectedReq.qty ? `, qty ${selectedReq.qty}` : ''}` +
                                      `${selectedReq.total_ctw ? `, total ${selectedReq.total_ctw} ct` : ''}. Please quote: ${reqUrl}`;
                        const waLink = buildWaLink(supp.contact_phone, waMsg);
                        return waLink ? (
                          <a key={sid} href={waLink} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100"
                            data-testid={`workspace-wa-${sid}`}>
                            <MessageCircle size={11} /> {supp.name}
                          </a>
                        ) : (
                          <span key={sid} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-400 text-[11px]" title="No phone">
                            <Phone size={10} /> {supp.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {reqQuotes.length === 0 ? (
                  <div className="text-center py-10 text-sm text-muted-foreground">
                    {isSupplier ? (
                      <>
                        <p>You haven't quoted on this requirement yet.</p>
                        {selectedReq.status === 'open' && (
                          <Button onClick={() => navigate(`/stones/${selectedReq.id}/quote`)} className="mt-3 bg-teal-600 hover:bg-teal-700">
                            <Send size={14} className="mr-1" /> Submit a quote
                          </Button>
                        )}
                      </>
                    ) : 'No quotes received yet.'}
                  </div>
                ) : (
                  reqQuotes.map(q => {
                    const qstatus = STONE_QUOTE_STATUSES[q.status] || { label: q.status, color: '' };
                    const isActive = q.id === selectedQuoteId;
                    return (
                      <button key={q.id}
                        onClick={() => setQuote(q.id)}
                        className={`relative w-full text-left rounded-xl bg-white border p-3 pl-4 transition-all hover:shadow-md ${
                          isActive ? 'border-teal-500 ring-2 ring-teal-100' :
                          q.status === 'rejected' ? 'border-slate-200 opacity-70' :
                          'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`workspace-quote-${q.id}`}>
                        {/* Status side-band — subtle vertical accent, classier than full bg tint */}
                        <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${
                          q.status === 'accepted' ? 'bg-emerald-500' :
                          q.status === 'rejected' ? 'bg-rose-400' :
                          q.status === 'revise_requested' ? 'bg-violet-500' :
                          'bg-amber-400'
                        }`} aria-hidden />
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm">{q.supplier_name}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded border ${qstatus.color}`}>{qstatus.label}</span>
                              {q.revision_count > 0 && <span className="text-[10px] text-violet-700">rev {q.revision_count}</span>}
                            </div>
                            {q.supplier_company && <div className="text-[11px] text-muted-foreground">{q.supplier_company}</div>}
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {q.certificate_type && q.certificate_type !== 'none' && (
                                <span className="uppercase font-medium">{q.certificate_type}</span>
                              )}
                              {q.certificate_number && <span> · #{q.certificate_number}</span>}
                              {q.qty_offered ? ` · Qty ${q.qty_offered}` : ''}
                              {q.total_ctw_offered ? ` · ${q.total_ctw_offered} ct` : ''}
                            </div>
                          </div>
                          <div className="text-right">
                            {q.price_total != null && (
                              <div className="text-lg font-bold text-teal-700 leading-tight">
                                {q.currency} {Number(q.price_total).toLocaleString()}
                              </div>
                            )}
                            {q.price_per_ct != null && (
                              <div className="text-[10px] text-muted-foreground">{q.currency} {Number(q.price_per_ct).toLocaleString()} / ct</div>
                            )}
                          </div>
                        </div>

                        {/* Supplier-provided images so admin can visually compare quotes
                            at a glance. Click any thumb to open full-size in a new tab.
                            Stops propagation so opening an image doesn't also flip the
                            chat to this quote. */}
                        {(q.stone_images?.length > 0 || q.certificate_image) && (
                          <div className="mt-2 flex gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                            {q.stone_images?.slice(0, 6).map((p, i) => (
                              <a key={i} href={getImageUrl(p)} target="_blank" rel="noreferrer"
                                className="block w-12 h-12 rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-teal-400 hover:shadow-md transition-all"
                                title={`Stone image ${i + 1}`}
                                data-testid={`quote-img-${q.id}-${i}`}>
                                <img src={getImageUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              </a>
                            ))}
                            {q.stone_images?.length > 6 && (
                              <span className="w-12 h-12 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-[10px] font-medium text-slate-500">
                                +{q.stone_images.length - 6}
                              </span>
                            )}
                            {q.certificate_image && (
                              <a href={getImageUrl(q.certificate_image)} target="_blank" rel="noreferrer"
                                className="block w-12 h-12 rounded-md overflow-hidden border-2 border-amber-300 bg-amber-50 hover:border-amber-500 hover:shadow-md transition-all"
                                title={`Certificate${q.certificate_type && q.certificate_type !== 'none' ? ` (${q.certificate_type.toUpperCase()})` : ''}`}
                                data-testid={`quote-cert-img-${q.id}`}>
                                <img src={getImageUrl(q.certificate_image)} alt="cert" className="w-full h-full object-cover" loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              </a>
                            )}
                          </div>
                        )}

                        {/* Revise request badge */}
                        {q.revise_request && q.status === 'revise_requested' && (
                          <div className="mt-2 bg-violet-50 border border-violet-200 rounded p-2 text-xs">
                            <div className="text-[10px] uppercase text-violet-700 font-medium">Awaiting revision</div>
                            {q.revise_request.target_price_per_ct != null && (
                              <div>Target/ct: {q.currency} {q.revise_request.target_price_per_ct}</div>
                            )}
                            {q.revise_request.target_price_total != null && (
                              <div>Target total: {q.currency} {Number(q.revise_request.target_price_total).toLocaleString()}</div>
                            )}
                            {q.revise_request.notes && <div className="text-slate-700 mt-1 whitespace-pre-wrap">{q.revise_request.notes}</div>}
                          </div>
                        )}

                        {/* Admin action row */}
                        {isAdmin && q.status === 'submitted' && (
                          <div className="mt-2 pt-2 border-t flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-2" disabled={acting === q.id} onClick={() => handleAccept(q)} data-testid={`accept-${q.id}`}>
                              {acting === q.id ? <Loader2 size={11} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />} Accept
                            </Button>
                            <Button size="sm" variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50 h-7 text-xs px-2" onClick={() => setReviseQuote(q)} data-testid={`revise-${q.id}`}>
                              <Send size={12} className="mr-1" /> Revise
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-500 h-7 text-xs px-2" onClick={() => handleReject(q)} data-testid={`reject-${q.id}`}>
                              <X size={12} className="mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </section>

        {/* === RIGHT PANE: chat thread === */}
        <section className={`${mobileView === 'right' ? 'block' : 'hidden'} lg:block lg:col-span-3 xl:col-span-3 bg-white lg:rounded-lg lg:border`} data-testid="right-pane">
          {selectedQuote ? (
            <div className="flex flex-col h-full">
              <div className="px-3 py-2 border-b flex items-center gap-2 text-xs">
                <MessageSquare size={12} className="text-slate-500" />
                <span className="font-medium truncate">{selectedQuote.supplier_name}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground truncate">{selectedQuote.currency} {selectedQuote.price_total != null ? Number(selectedQuote.price_total).toLocaleString() : '—'}</span>
              </div>
              <div className="flex-1 min-h-0">
                <StoneQuoteChat quoteId={selectedQuote.id} currentUser={user} />
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-6 text-center text-xs text-muted-foreground">
              {selectedReq ? 'Pick a quote in the middle pane to negotiate.' : 'Select a requirement first.'}
            </div>
          )}
        </section>
      </div>

      <ReviseQuoteDialog
        open={!!reviseQuote}
        onOpenChange={(o) => { if (!o) setReviseQuote(null); }}
        quote={reviseQuote}
        onSubmit={handleRevise}
      />
    </CollapsibleLayout>
  );
}
